import { isFulfilled, type TypedStartListening } from '@reduxjs/toolkit'
import { QueryStatus } from '@reduxjs/toolkit/query'

import {
  GATEWAY_URLS,
  baseApi,
  loginUrl,
  isTransportError,
  maintenanceApi,
  maintenanceObserved,
  resourceScopeCleared,
  resourceScopeTag,
  sessionApi,
  sessionDenied,
  sessionExpired,
  sessionSignedOut,
  sessionStarted,
  userScopeCleared,
  type ClientRuntimeExtra,
  type HttpClient,
  type LiveChannel,
  type ModuleListener,
  type PersistenceController,
} from '@krp/client-runtime'

import type { AppDispatch, RootState } from '../app/types'
import { aqsiModule, oneCModule, platformModule } from './modules'
import { signInRequested, signOutRequested, signedOutElsewhere } from './session'
import type { SessionChannel } from './sessionChannel'

type StartListening = TypedStartListening<RootState, AppDispatch, ClientRuntimeExtra>

export interface ListenerDependencies {
  persistence: PersistenceController<RootState>
  http: HttpClient
  leaveTo: (url: string) => void
  channel: SessionChannel
  /** Живой канал вкладки (LIVE): работает, пока сессия активна. */
  live: LiveChannel
}

/** Подключить listeners модуля: он получает только свой срез (F05). */
function registerModule<S>(
  startListening: StartListening,
  listeners: readonly ModuleListener<S>[],
  selectModule: (state: RootState) => S,
): void {
  for (const listener of listeners) {
    startListening({
      predicate: (action) => listener.predicate(action),
      effect: (action, api) =>
        listener.effect(action, {
          dispatch: api.dispatch,
          getModuleState: () => selectModule(api.getState()),
          signal: api.signal,
        }),
    })
  }
}

/**
 * Сессия снова активна: запросы, упавшие на 401, пока она была «истекшей», сами не
 * перечитаются, и экран остался бы с ошибкой. Перечитываем их тем же ключом кэша.
 */
type Initiate = (arg: unknown, options: { subscribe: boolean; forceRefetch: boolean }) => unknown

/** Запуск запроса у конечной точки RTK Query: у каждой она своя, а перечитать нужно любую из упавших. */
function initiateOf(endpoint: unknown): Initiate | undefined {
  if (typeof endpoint !== 'object' || endpoint === null || !('initiate' in endpoint)) return undefined
  return typeof endpoint.initiate === 'function' ? (endpoint.initiate as Initiate) : undefined
}

function refetchUnauthorized(api: { getState: () => RootState; dispatch: AppDispatch }, retried: Set<string>): void {
  const endpoints: Record<string, unknown> = baseApi.endpoints
  for (const [cacheKey, query] of Object.entries(api.getState().api.queries)) {
    if (query === undefined || query.status !== QueryStatus.rejected) continue
    if (!isTransportError(query.error) || query.error.kind !== 'unauthorized') continue
    // Повтор уже был, и запрос снова получил 401: дело не в сессии, повторять без конца нельзя.
    if (retried.has(cacheKey)) continue
    const initiate = initiateOf(endpoints[query.endpointName])
    if (initiate === undefined) continue
    retried.add(cacheKey)
    api.dispatch(initiate(query.originalArgs, { subscribe: false, forceRefetch: true }) as never)
  }
}

/** Ключ кэша запроса, который завершился успешно; у остальных действий его нет. */
function fulfilledQueryKey(action: unknown): string | undefined {
  if (!isFulfilled(action)) return undefined
  const arg: unknown = action.meta.arg
  if (typeof arg !== 'object' || arg === null || !('queryCacheKey' in arg)) return undefined
  return typeof arg.queryCacheKey === 'string' ? arg.queryCacheKey : undefined
}

/**
 * Сценарии оболочки: границы области пользователя и ресурса (D-075), вход и
 * выход. Слайсы пакетов чистят себя сами по `userScopeCleared` /
 * `resourceScopeCleared`; здесь — кэш запросов, хранилище и transport.
 */
export function registerListeners(startListening: StartListening, deps: ListenerDependencies): void {
  /** Запросы, которые уже перечитывались после возврата сессии: успех снимает отметку. */
  const retried = new Set<string>()
  startListening({
    predicate: (action) => fulfilledQueryKey(action) !== undefined,
    effect: (action) => {
      const key = fulfilledQueryKey(action)
      if (key !== undefined) retried.delete(key)
    },
  })

  /** Закрыть область пользователя: записи, токен антифоргери, намерение, кэш; конечное состояние signedOut. */
  const closeUserScope = (dispatch: AppDispatch, userId: string | null) => {
    if (userId !== null) deps.persistence.forgetUser(userId)
    deps.http.resetCsrf()
    dispatch(userScopeCleared())
    dispatch(sessionSignedOut())
    dispatch(baseApi.util.resetApiState())
  }

  // Gateway сказал, кто вошёл: открыть область этого пользователя.
  startListening({
    matcher: sessionApi.endpoints.sessionMe.matchFulfilled,
    effect: (action, api) => {
      const { userId } = action.payload
      if (api.getState().session.status === 'signedOut') return
      const previous = api.getState().session.scopeUserId
      if (previous === userId) {
        // Тот же человек снова вошёл (например, в соседней вкладке): сессия снова активна.
        if (api.getState().session.status !== 'active') {
          api.dispatch(sessionStarted({ userId }))
          refetchUnauthorized(api, retried)
        }
        return
      }
      if (previous !== null) {
        // Другой человек на том же устройстве: прежняя область уходит целиком.
        deps.persistence.forgetUser(previous)
        deps.http.resetCsrf()
        api.dispatch(userScopeCleared())
        api.dispatch(baseApi.util.resetApiState())
      }
      api.dispatch(sessionStarted({ userId }))
      deps.persistence.hydrateUser(api.dispatch, userId)
    },
  })

  // «Кто я» отказал: 401 — нужен вход; 403 — ролей приложения нет (на вход НЕ уводить: петля).
  startListening({
    matcher: sessionApi.endpoints.sessionMe.matchRejected,
    effect: (action, api) => {
      const error = action.payload
      if (isTransportError(error) && error.kind === 'forbidden') api.dispatch(sessionDenied())
      else if (isTransportError(error) && error.kind === 'unauthorized') api.dispatch(sessionExpired())
    },
  })

  // Выход: стереть записи пользователя ДО сброса состояния, сообщить соседним вкладкам, уйти на выход Gateway.
  startListening({
    actionCreator: signOutRequested,
    effect: (_action, api) => {
      const userId = api.getState().session.scopeUserId
      closeUserScope(api.dispatch, userId)
      if (userId !== null) deps.channel.announceSignOut(userId)
      deps.leaveTo(GATEWAY_URLS.logout)
    },
  })

  // Выход в соседней вкладке: та же очистка здесь, без ухода со страницы.
  startListening({
    actionCreator: signedOutElsewhere,
    effect: (action, api) => {
      const userId = api.getState().session.scopeUserId
      if (userId !== action.payload.userId) return
      closeUserScope(api.dispatch, userId)
    },
  })

  startListening({
    actionCreator: signInRequested,
    effect: (action, api) => {
      // Отложенная запись (вкладка, фильтр) не должна потеряться при уходе на вход.
      deps.persistence.flush(api.getState())
      deps.leaveTo(loginUrl(action.payload.returnUrl))
    },
  })

  // Ресурс вышел из работы: всё, что кэш помнит о нём, перечитывается/уходит; соседний rid не трогается.
  startListening({
    actionCreator: resourceScopeCleared,
    effect: (action, api) => {
      api.dispatch(baseApi.util.invalidateTags([resourceScopeTag(action.payload.resourceId)]))
    },
  })

  // Живой канал — только при активной сессии: выход, истёкшая или отозванная сессия его закрывают.
  startListening({
    predicate: (_action, current, previous) => current.session.status !== previous.session.status,
    effect: (_action, api) => {
      if (api.getState().session.status === 'active') deps.live.start()
      else deps.live.stop()
    },
  })

  // RP-MNT: сервер назвал состояние режима обновления — запомнить его на устройстве (переживёт перезапуск сервера на обновлении).
  startListening({
    matcher: maintenanceApi.endpoints.maintenanceState.matchFulfilled,
    effect: (action, api) => {
      api.dispatch(maintenanceObserved({ state: action.payload, at: Date.now() }))
    },
  })

  registerModule(startListening, platformModule.listeners, (state) => state.platform)
  registerModule(startListening, oneCModule.listeners, (state) => state.onec)
  registerModule(startListening, aqsiModule.listeners, (state) => state.aqsi)
}
